import React, { useMemo } from 'react'
import {
  Vertical,
  Horizontal,
  Texto,
  BBDTag,
  MaterialIcon,
  GraviGrid,
  addCommasToNumber,
} from '@gravitate-js/excalibrr'
import { useFormatter } from '@api/useFormatter'
import type { MatchDetailResponse } from '../../../../../api/useSupplierReconciliation/useMatchDetail'
import type { components } from '../../../../../types/api/v1'
import { useMatchSummaryData } from '../hooks/useMatchSummaryData'
import {
  createMatchSummaryColumnDefs,
  type ConsistentCurrencyFormatters,
} from '../columnDefs/detailColumnDefs/columns/matchSummaryColumns'
import { calculateMatchSummaryPrecisions } from '../utils/precisionCalculator'
import {
  formatCurrencyConsistent,
  formatVarianceConsistent,
  formatPerUnitConsistent,
} from '../columnDefs/detailColumnDefs/formatters'

type BolCandidate = components['schemas']['BolCandidate']
type InvoiceCandidate = components['schemas']['InvoiceCandidate']

interface ManageMatchSummaryGridProps {
  matchDetails: MatchDetailResponse
  currentMatchDocuments: {
    invoices: InvoiceCandidate[]
    bols: BolCandidate[]
  }
  pendingItems?: (InvoiceCandidate | BolCandidate)[]
  onPerUnitClick?: (priceId: string) => void
}

export function ManageMatchSummaryGrid({
  matchDetails,
  currentMatchDocuments,
  pendingItems = [],
  onPerUnitClick,
}: ManageMatchSummaryGridProps) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)
  // Convert current match changes to pendingItems format
  const computedPendingItems = useMemo(() => {
    // Get IDs from original match
    const originalInvoiceIds = new Set(
      matchDetails.document_detail_rows
        ?.filter((row) => row.document_type === 'invoice')
        .map((row) => row.document_id) || []
    )
    const originalBolIds = new Set(
      matchDetails.document_detail_rows?.filter((row) => row.document_type === 'bol').map((row) => row.document_id) ||
        []
    )

    const newPendingItems: any[] = []

    // Add new invoices as pending items
    currentMatchDocuments.invoices.forEach((invoice) => {
      if (!originalInvoiceIds.has(invoice.invoice_id)) {
        newPendingItems.push({
          id: invoice.invoice_id,
          type: 'invoice',
          data: {
            invoice_number: invoice.invoice_number,
            invoice_date: invoice.invoice_date,
            terminal: invoice.terminal,
            details: invoice.details || [],
            store_number: invoice.store_number,
            invoice_total: invoice.invoice_total,
          },
        })
      }
    })

    // Add new BOLs as pending items
    currentMatchDocuments.bols.forEach((bol) => {
      if (!originalBolIds.has(bol.bol_id)) {
        // Calculate totals from products array if top-level totals are missing
        const products = bol.products || []
        const calculatedProductTotal = products.reduce((sum, product) => sum + (product.product_total || 0), 0)
        const calculatedTaxTotal = products.reduce((sum, product) => sum + (product.tax_total || 0), 0)
        const calculatedTotal = calculatedProductTotal + calculatedTaxTotal

        newPendingItems.push({
          id: bol.bol_id,
          type: 'bol',
          data: {
            bol_number: bol.bol_number,
            bol_date: bol.bol_date,
            terminal_name: bol.terminal_name,
            terminal: bol.terminal,
            products: products,
            store_number: bol.store_number,
            bol_total: bol.bol_total,
            product_total: bol.product_total || calculatedProductTotal,
            tax_total: bol.tax_total || calculatedTaxTotal,
            total: bol.total || calculatedTotal,
          },
        })
      }
    })

    return [...pendingItems, ...newPendingItems]
  }, [matchDetails, currentMatchDocuments, pendingItems])

  // Create a filtered match details that excludes removed items but preserves original structure
  const filteredMatchDetails = useMemo(() => {
    const currentInvoiceIds = new Set(currentMatchDocuments.invoices.map((inv) => inv.invoice_id))
    const currentBolIds = new Set(currentMatchDocuments.bols.map((bol) => bol.bol_id))

    const filteredRows =
      matchDetails.document_detail_rows?.filter((row) => {
        if (row.document_type === 'invoice') {
          return currentInvoiceIds.has(row.document_id)
        } else {
          return currentBolIds.has(row.document_id)
        }
      }) || []

    // Check if the document set has changed from the original
    const originalInvoiceIds = new Set(
      matchDetails.document_detail_rows
        ?.filter((row) => row.document_type === 'invoice')
        .map((row) => row.document_id) || []
    )
    const originalBolIds = new Set(
      matchDetails.document_detail_rows?.filter((row) => row.document_type === 'bol').map((row) => row.document_id) ||
        []
    )

    const documentsChanged =
      currentInvoiceIds.size !== originalInvoiceIds.size ||
      currentBolIds.size !== originalBolIds.size ||
      ![...currentInvoiceIds].every((id) => originalInvoiceIds.has(id)) ||
      ![...currentBolIds].every((id) => originalBolIds.has(id))

    // If documents have changed, clear variance fields to force recalculation
    // Otherwise preserve original variance fields from API
    if (documentsChanged) {
      return {
        ...matchDetails,
        document_detail_rows: filteredRows,
        // Clear variance fields to force recalculation
        total_variance: undefined,
        product_total_variance: undefined,
        tax_total_variance: undefined,
        gross_volume_variance: undefined,
        net_volume_variance: undefined,
        per_unit_variance: undefined,
      }
    } else {
      return {
        ...matchDetails,
        document_detail_rows: filteredRows,
      }
    }
  }, [matchDetails, currentMatchDocuments])

  // Use the existing hook like MatchSummaryGrid does - with original structure + pendingItems
  const {
    matchSummaryData,
    matchSummaryGridHeight: hookGridHeight,
    totalVarianceWithPending,
  } = useMatchSummaryData(filteredMatchDetails, computedPendingItems)

  // Calculate precision requirements based on the actual data
  const precisionData = useMemo(() => {
    return calculateMatchSummaryPrecisions(matchSummaryData)
  }, [matchSummaryData])

  // Create dynamic currency formatters with consistent precision support
  const currencyFormatters: ConsistentCurrencyFormatters = useMemo(
    () => ({
      formatCurrency: (amount?: number) => {
        if (amount === null || amount === undefined) return `${currencySymbol}0.00`
        if (amount === 0) return `${currencySymbol}0.00`
        const sign = amount < 0 ? '-' : ''
        return `${sign}${currencySymbol}${addCommasToNumber(Math.abs(amount), 2)}`
      },
      formatVariance: (value: number, showSign?: boolean) => {
        if (value === 0) return `${currencySymbol}0.00`
        const sign = showSign && value !== 0 ? (value > 0 ? '+' : '-') : ''
        return `${sign}${currencySymbol}${addCommasToNumber(Math.abs(value), 2)}`
      },
      formatPerUnit: (amount?: number) => {
        if (amount === null || amount === undefined) return `${currencySymbol}0.0000`
        if (amount === 0) return `${currencySymbol}0.0000`
        const sign = amount < 0 ? '-' : ''
        return `${sign}${currencySymbol}${Math.abs(amount).toFixed(4)}`
      },
      // Consistent formatters
      formatCurrencyConsistent: (amount?: number, precision?: number) => {
        return formatCurrencyConsistent(amount || 0, precision || precisionData.precisions.currency, currencySymbol)
      },
      formatVarianceConsistent: (value: number, precision?: number, showSign?: boolean) => {
        return formatVarianceConsistent(
          value,
          precision || precisionData.precisions.currency,
          currencySymbol,
          showSign || false
        )
      },
      formatPerUnitConsistent: (amount?: number, precision?: number) => {
        return formatPerUnitConsistent(amount, precision || precisionData.precisions.perUnit, currencySymbol, false)
      },
      currencySymbol,
    }),
    [currencySymbol, precisionData.precisions]
  )

  // Create column definitions with precision support
  const matchSummaryColumns = useMemo(() => {
    return createMatchSummaryColumnDefs(
      onPerUnitClick,
      matchDetails.supplier_id,
      matchDetails.supplier_name,
      matchDetails.status,
      currencyFormatters,
      precisionData.precisions
    )
  }, [
    onPerUnitClick,
    matchDetails.supplier_id,
    matchDetails.supplier_name,
    matchDetails.status,
    currencyFormatters,
    precisionData.precisions,
  ])

  // Use the variance calculated by the hook
  const totalVariance =
    totalVarianceWithPending !== undefined ? totalVarianceWithPending : filteredMatchDetails?.total_variance || 0
  const hasVariance = Math.abs(totalVariance) >= 0.01

  // If no data, show a simple message
  if (!matchSummaryData || matchSummaryData.length === 0) {
    return (
      <Vertical
        flex='0'
        style={{
          overflow: 'visible',
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Texto category='h4' weight='bold'>
          MATCH SUMMARY
        </Texto>
        <Texto>No match summary data available.</Texto>
      </Vertical>
    )
  }

  return (
    <Vertical flex='0' style={{ overflow: 'visible', background: '#f5f5f5' }}>
      <Vertical
        flex='0'
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Horizontal justifyContent='space-between' alignItems='center' style={{ marginBottom: 8 }}>
          <Texto category='h4' weight='bold'>
            MATCH SUMMARY
          </Texto>
          <Horizontal gap={16} verticalCenter>
            <BBDTag
              text={
                hasVariance
                  ? `Variance: ${totalVariance >= 0 ? '+' : '-'}${currencySymbol}${addCommasToNumber(Math.abs(totalVariance), 2)}`
                  : `No Variance: ${currencySymbol}0.00`
              }
              appearance={hasVariance ? (Math.abs(totalVariance) < 0.01 ? 'success' : 'danger') : undefined}
              size='small'
            />
            {pendingItems.length > 0 && (
              <BBDTag text={`${pendingItems.length} pending`} appearance='warning' size='small' />
            )}
          </Horizontal>
        </Horizontal>
      </Vertical>

      <Vertical
        style={{
          height: hookGridHeight,
          borderRadius: 0,
          overflow: 'hidden',
        }}
      >
        <GraviGrid
          columnDefs={matchSummaryColumns}
          rowData={matchSummaryData}
          agPropOverrides={{
            domLayout: 'normal',
            suppressMovableColumns: true,
            suppressColumnVirtualisation: true,
            suppressRowVirtualisation: true,
            animateRows: false,
            getRowId: (params) => params.data._gridRowId,
            getRowHeight: (params) => {
              if (!params.data) return 30
              return params.data.type === 'total' ? 40 : 30
            },
            getRowStyle: (params) => {
              if (!params.data) return {}

              if (params.data.type === 'total') {
                return {
                  background: '#f0f0f0',
                  fontWeight: 'bold',
                  borderTop: '2px solid #d9d9d9',
                  borderBottom: '2px solid #d9d9d9',
                }
              }

              if (params.data.type === 'comparison') {
                return {
                  background: '#fafafa',
                  fontStyle: 'italic',
                  borderTop: '1px solid #e8e8e8',
                  borderBottom: '1px solid #e8e8e8',
                }
              }

              if (params.data._isPending) {
                return {
                  background: '#fff7e6',
                  borderLeft: '3px solid #ffa940',
                }
              }

              return {}
            },
          }}
        />
      </Vertical>
    </Vertical>
  )
}
